// Reusable document (PDF/Print) HTML shell for THE WOODLAND ERP.
// All downloadable documents (BOQ, Invoice, Statement, Project export, DPR)
// use these helpers so the branding stays consistent everywhere.
import { BRAND } from "@/lib/brand";

// Dynamic overrides pushed by the app after fetching /api/company.
// Falls back to the static BRAND config for offline / cold-start scenarios.
let CURRENT = { ...BRAND };
export function setDocumentBrand(company) {
  if (!company) return;
  CURRENT = {
    name: company.name || BRAND.name,
    tagline: company.tagline || BRAND.tagline,
    logoUrl: company.logo_url || company.logoUrl || BRAND.logoUrl,
    address: company.address || BRAND.address,
    phone: company.phone || BRAND.phone,
    email: company.email || BRAND.email,
    website: company.website || BRAND.website,
  };
}

const escapeHtml = (v = "") =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export function documentBaseCss() {
  return `
    :root { color-scheme: light; }
    @page { size: A4; margin: 15mm 15mm 20mm 15mm; }
    * { box-sizing: border-box; }
    body { font-family: 'Manrope', -apple-system, Segoe UI, Roboto, sans-serif; color:#1A1918; margin:0; padding:32px 36px; background:#fff; font-size:12px; line-height:1.5; }
    .doc-header { display:flex; justify-content:space-between; align-items:flex-start; gap:24px; border-bottom:2px solid #1A1918; padding-bottom:14px; margin-bottom:20px; }
    .doc-brand { display:flex; align-items:center; gap:14px; }
    .doc-brand img { height:56px; width:auto; object-fit:contain; }
    .doc-brand .name { font-size:22px; font-weight:700; letter-spacing:0.5px; margin:0; line-height:1.1; }
    .doc-brand .tagline { font-size:9px; letter-spacing:2px; text-transform:uppercase; color:#73706A; margin-top:4px; }
    .doc-brand .contact { font-size:9.5px; color:#73706A; margin-top:6px; line-height:1.5; }
    .doc-meta { text-align:right; font-size:11px; color:#73706A; }
    .doc-meta .title { font-size:18px; font-weight:700; color:#1A1918; letter-spacing:0.5px; }
    .doc-meta .num { font-family:monospace; font-size:11px; margin-top:2px; }
    .doc-footer { position:fixed; bottom:8mm; left:15mm; right:15mm; padding-top:8px; border-top:1px solid #E2DFD8; display:flex; justify-content:space-between; align-items:center; font-size:9px; color:#A8A6A0; text-transform:uppercase; letter-spacing:1.2px; }
    .doc-footer .center { text-align:center; flex:1; }
    .noprint { text-align:center; margin-top:30px; }
    .noprint button { background:#1A1918; color:#fff; padding:10px 26px; border:0; border-radius:6px; font-weight:600; cursor:pointer; font-size:13px; letter-spacing:0.5px; }
    @media print { .noprint { display:none } .doc-footer { position:fixed; } }
    table { width:100%; border-collapse:collapse; margin-top:6px; }
    th, td { padding:8px 10px; text-align:left; font-size:11px; border-bottom:1px solid #E2DFD8; }
    th { background:#F0EFEB; text-transform:uppercase; font-size:9.5px; letter-spacing:1.5px; color:#1A1918; }
    .right { text-align:right; }
    h1 { font-size:24px; margin:0; }
    h2 { font-size:13px; margin:22px 0 8px; color:#1A1918; text-transform:uppercase; letter-spacing:2px; border-bottom:1px solid #E2DFD8; padding-bottom:5px; }
  `;
}

/**
 * Build the branded document header HTML.
 * meta = { title, number, date }
 */
export function documentHeader(meta = {}) {
  const { title = "", number = "", date = "" } = meta;
  return `
    <div class="doc-header">
      <div class="doc-brand">
        <img src="${CURRENT.logoUrl}" alt="${escapeHtml(CURRENT.name)}" />
        <div>
          <div class="name">${escapeHtml(CURRENT.name)}</div>
          <div class="tagline">${escapeHtml(CURRENT.tagline)}</div>
          <div class="contact">
            ${escapeHtml(CURRENT.address)}<br/>
            +91 ${escapeHtml(CURRENT.phone)} · ${escapeHtml(CURRENT.email)}<br/>
            ${escapeHtml(CURRENT.website)}
          </div>
        </div>
      </div>
      <div class="doc-meta">
        ${title ? `<div class="title">${escapeHtml(title)}</div>` : ""}
        ${number ? `<div class="num">${escapeHtml(number)}</div>` : ""}
        ${date ? `<div>${escapeHtml(date)}</div>` : ""}
      </div>
    </div>
  `;
}

/** Fixed footer at the bottom of the printed page. */
export function documentFooter() {
  const now = new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  return `
    <div class="doc-footer">
      <div>Generated by ${escapeHtml(CURRENT.name)} ERP</div>
      <div class="center">${escapeHtml(now)}</div>
      <div>${escapeHtml(String(CURRENT.website || "").replace(/^https?:\/\//, "").replace(/\/$/, ""))}</div>
    </div>
  `;
}

/**
 * Wrap arbitrary body HTML inside a fully branded printable document.
 * meta = { title, number, date, subject (browser tab title), extraCss }
 */
export function buildBrandedDocument(bodyHtml, meta = {}) {
  const { subject = CURRENT.name, extraCss = "", showPrintButton = true, title, number, date } = meta;
  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>${escapeHtml(subject)}</title>
    <style>${documentBaseCss()}${extraCss}</style>
    </head><body>
    ${documentHeader({ title, number, date })}
    ${bodyHtml}
    ${documentFooter()}
    ${showPrintButton ? '<div class="noprint"><button onclick="window.print()">Print / Save as PDF</button></div>' : ""}
    </body></html>`;
}

/**
 * Open a new browser tab and print a fully branded document.
 * Returns the window ref (or null if popup blocked).
 */
export function openBrandedPrintWindow(bodyHtml, meta = {}) {
  const w = window.open("", "_blank");
  if (!w) return null;
  w.document.write(buildBrandedDocument(bodyHtml, meta));
  w.document.close();
  setTimeout(() => {
    try { w.print(); } catch (e) { /* ignore */ }
  }, 350);
  return w;
}
